import { createFileRoute, useNavigate, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { classInputSchema, type ClassInput } from '@pirouette/shared';
import { useCreateClass } from '@/data/classes';
import { useLocations, usePrograms } from '@/data/programs';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { toast } from '@/lib/toast';

export const Route = createFileRoute('/admin/classes/new')({
  component: NewClass,
});

function NewClass() {
  const navigate = useNavigate();
  const createClass = useCreateClass();
  const programs = usePrograms();
  const locations = useLocations();

  const termsQuery = useQuery({
    queryKey: ['terms'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('terms')
        .select('id, name, start_date, end_date, is_current')
        .order('start_date', { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ClassInput>({
    resolver: zodResolver(classInputSchema),
    mode: 'onBlur',
    defaultValues: {
      capacity: 18,
      tuition_cents: 12500,
      weekday: 'tuesday',
      start_time: '16:00',
      end_time: '17:00',
      delivery_mode: 'in_person',
    },
  });

  async function onSubmit(values: ClassInput) {
    try {
      const created = await createClass.mutateAsync(values);
      toast({ title: 'Class added', description: created.name, variant: 'success' });
      await navigate({ to: '/admin/classes/$id', params: { id: created.id } });
    } catch (err) {
      toast({
        title: "Couldn't save the class",
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'error',
      });
    }
  }

  const optionalId = (value: unknown) => (value === '' ? undefined : value);

  return (
    <div className="max-w-3xl space-y-8">
      <header>
        <Link
          to="/admin/classes"
          className="text-[var(--color-ink-muted)] text-[var(--text-sm)] hover:text-[var(--color-ink)]"
        >
          ← Classes
        </Link>
        <p className="mt-2 tracking-[var(--tracking-wide)] text-[var(--color-ink-muted)] text-[var(--text-sm)] uppercase">
          New
        </p>
        <h1 className="mt-2">Add a class</h1>
      </header>

      <form onSubmit={(e) => void handleSubmit(onSubmit)(e)} noValidate className="grid gap-6">
        <fieldset className="surface-elevated grid gap-5 p-6">
          <legend className="px-2 tracking-[var(--tracking-wide)] text-[var(--color-ink-muted)] text-[var(--text-sm)] uppercase">
            What
          </legend>

          <div>
            <Label htmlFor="class-name">Name</Label>
            <Input id="class-name" aria-invalid={!!errors.name} {...register('name')} />
            {errors.name && (
              <p role="alert" className="mt-1.5 text-[var(--color-danger)] text-[var(--text-sm)]">
                {errors.name.message}
              </p>
            )}
          </div>

          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              <Label htmlFor="class-program">Program</Label>
              <select
                id="class-program"
                className="h-11 w-full rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] px-3"
                defaultValue=""
                {...register('program_id', { setValueAs: optionalId })}
              >
                <option value="">
                  {programs.isLoading ? 'Loading programs…' : 'No program selected'}
                </option>
                {programs.data?.map((program) => (
                  <option key={program.id} value={program.id}>
                    {program.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label htmlFor="class-location">Location</Label>
              <select
                id="class-location"
                className="h-11 w-full rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] px-3"
                defaultValue=""
                {...register('location_id', { setValueAs: optionalId })}
              >
                <option value="">
                  {locations.isLoading ? 'Loading locations…' : 'No location selected'}
                </option>
                {locations.data?.map((location) => (
                  <option key={location.id} value={location.id}>
                    {location.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid gap-5 sm:grid-cols-3">
            <div>
              <Label htmlFor="class-style">Style</Label>
              <select
                id="class-style"
                className="h-11 w-full rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] px-3"
                {...register('style')}
              >
                <option value="ballet">Ballet</option>
                <option value="pointe">Pointe</option>
                <option value="pre-pointe">Pre-pointe</option>
                <option value="jazz">Jazz</option>
                <option value="tap">Tap</option>
                <option value="hip-hop">Hip-hop</option>
                <option value="contemporary">Contemporary</option>
                <option value="lyrical">Lyrical</option>
                <option value="modern">Modern</option>
                <option value="musical-theatre">Musical theatre</option>
                <option value="acro">Acro</option>
                <option value="ballroom">Ballroom</option>
                <option value="bollywood">Bollywood</option>
                <option value="cheer">Cheer</option>
                <option value="tumbling">Tumbling</option>
                <option value="gymnastics">Gymnastics</option>
                <option value="conditioning">Conditioning</option>
                <option value="pilates">Pilates</option>
                <option value="voice">Voice</option>
                <option value="piano">Piano</option>
                <option value="violin">Violin</option>
                <option value="acting">Acting</option>
                <option value="creative-movement">Creative movement</option>
                <option value="adult-fitness">Adult fitness</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <Label htmlFor="class-level">Level</Label>
              <select
                id="class-level"
                className="h-11 w-full rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] px-3"
                {...register('level')}
              >
                <option value="pre">Pre</option>
                <option value="beginner">Beginner</option>
                <option value="intermediate">Intermediate</option>
                <option value="advanced">Advanced</option>
                <option value="comp">Comp</option>
              </select>
            </div>
            <div>
              <Label htmlFor="class-delivery">Delivery</Label>
              <select
                id="class-delivery"
                className="h-11 w-full rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] px-3"
                {...register('delivery_mode')}
              >
                <option value="in_person">In person</option>
                <option value="online">Online</option>
                <option value="hybrid">Hybrid</option>
                <option value="offsite">Offsite</option>
              </select>
            </div>
          </div>

          <div>
            <Label htmlFor="class-term">Term</Label>
            <select
              id="class-term"
              className="h-11 w-full rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] px-3"
              aria-invalid={!!errors.term_id}
              defaultValue=""
              {...register('term_id')}
            >
              <option value="" disabled>
                {termsQuery.isLoading ? 'Loading terms…' : 'Select a term…'}
              </option>
              {termsQuery.data?.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name} ({t.start_date} → {t.end_date})
                </option>
              ))}
            </select>
            {errors.term_id && (
              <p role="alert" className="mt-1.5 text-[var(--color-danger)] text-[var(--text-sm)]">
                {errors.term_id.message}
              </p>
            )}
          </div>
        </fieldset>

        <fieldset className="surface-elevated grid gap-5 p-6">
          <legend className="px-2 tracking-[var(--tracking-wide)] text-[var(--color-ink-muted)] text-[var(--text-sm)] uppercase">
            When + where
          </legend>

          <div className="grid gap-5 sm:grid-cols-3">
            <div>
              <Label htmlFor="class-weekday">Weekday</Label>
              <select
                id="class-weekday"
                className="h-11 w-full rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] px-3"
                {...register('weekday')}
              >
                <option value="monday">Monday</option>
                <option value="tuesday">Tuesday</option>
                <option value="wednesday">Wednesday</option>
                <option value="thursday">Thursday</option>
                <option value="friday">Friday</option>
                <option value="saturday">Saturday</option>
                <option value="sunday">Sunday</option>
              </select>
            </div>
            <div>
              <Label htmlFor="class-start">Start</Label>
              <Input id="class-start" type="time" {...register('start_time')} />
            </div>
            <div>
              <Label htmlFor="class-end">End</Label>
              <Input id="class-end" type="time" {...register('end_time')} />
              {errors.end_time && (
                <p role="alert" className="mt-1.5 text-[var(--color-danger)] text-[var(--text-sm)]">
                  {errors.end_time.message}
                </p>
              )}
            </div>
          </div>

          <div className="grid gap-5 sm:grid-cols-3">
            <div>
              <Label htmlFor="class-room">Room</Label>
              <Input id="class-room" {...register('room')} />
            </div>
            <div>
              <Label htmlFor="class-capacity">Capacity</Label>
              <Input
                id="class-capacity"
                type="number"
                min="1"
                max="100"
                {...register('capacity', { valueAsNumber: true })}
              />
            </div>
            <div>
              <Label htmlFor="class-tuition">Tuition (cents)</Label>
              <Input
                id="class-tuition"
                type="number"
                min="0"
                {...register('tuition_cents', { valueAsNumber: true })}
              />
            </div>
          </div>
        </fieldset>

        <div className="flex gap-3">
          <Button type="submit" disabled={isSubmitting} aria-busy={isSubmitting}>
            {isSubmitting ? 'Saving…' : 'Save class'}
          </Button>
          <Link to="/admin/classes">
            <Button variant="ghost" type="button">
              Cancel
            </Button>
          </Link>
        </div>
      </form>
    </div>
  );
}
